(() => {
  if (window.sfSlider) return;
  // All sections share navigation, timer and interaction rules. Embla alone owns
  // gesture recognition, snapping and suppression of clicks after a drag.
  window.sfSlider = (root, carousel, { viewport, slides, label = 'Slide', onSelect = () => {}, onSettle = () => {}, onResize = () => {} } = {}) => {
    const events = new AbortController();
    const on = (node, type, fn, options = {}) => node?.addEventListener(type, fn, { ...options, signal: events.signal });
    const controls = root.querySelector('[data-slider-controls]');
    const dotsHost = root.querySelector('[data-slider-dots]');
    const previous = root.querySelector('[data-slider-previous]');
    const next = root.querySelector('[data-slider-next]');
    const timerControl = root.querySelector('[data-slider-timer]');
    const status = root.querySelector('[data-slider-status]');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const interval = Math.max(4000, Math.min(15000, Number(root.dataset.interval) || 6000));
    let dots = [], timer, frame, remaining = interval, started = null;
    let hovered = false, userPaused = false, editorPaused = false, dragging = false, visible = false, requested = false, announce = false;
    const count = () => carousel.scrollSnapList().length;
    const paint = () => {
      const elapsed = started === null ? 0 : performance.now() - started;
      root.style.setProperty('--sf-carousel-progress', String(Math.min(1, 1 - Math.max(0, remaining - elapsed) / interval)));
    };
    const stop = () => {
      clearTimeout(timer); cancelAnimationFrame(frame);
      if (started !== null) remaining = Math.max(0, remaining - (performance.now() - started));
      started = null; paint(); root.dataset.autoplayState = 'paused';
    };
    const start = () => {
      stop();
      if (root.dataset.autoplay !== 'true' || count() < 2 || reduced.matches || userPaused || editorPaused || dragging || !visible || document.hidden ||
          (hovered && !requested) || (root.contains(document.activeElement) && !(requested && document.activeElement === timerControl))) return;
      started = performance.now(); root.dataset.autoplayState = 'playing';
      const tick = () => { paint(); frame = requestAnimationFrame(tick); };
      frame = requestAnimationFrame(tick);
      timer = setTimeout(() => show((carousel.selectedScrollSnap() + 1) % count(), false), remaining);
    };
    const indicators = () => {
      const index = carousel.selectedScrollSnap();
      dots.forEach((dot, i) => { dot.classList.toggle('is-active', i === index); dot.setAttribute('aria-pressed', String(i === index)); });
      if (dots[index] && dotsHost) {
        const active = dots[index].getBoundingClientRect(), host = dotsHost.getBoundingClientRect();
        if (active.left < host.left || active.right > host.right) dotsHost.scrollLeft += active.left - host.left - (host.width - active.width) / 2;
      }
      if (previous) previous.disabled = !carousel.canScrollPrev();
      if (next) next.disabled = !carousel.canScrollNext();
      root.dataset.sliderIndex = String(index);
      onSelect(index);
    };
    const accessibility = () => {
      if (dragging) return;
      // Product pages can contain several cards; all visible cards stay operable.
      const visibleSlides = [...carousel.slidesInView(), ...(carousel.internalEngine().slideRegistry[carousel.selectedScrollSnap()] || [])];
      slides.forEach((slide, i) => {
        const active = visibleSlides.includes(i);
        if (!active && slide.contains(document.activeElement)) viewport.focus({ preventScroll: true });
        slide.inert = !active; slide.setAttribute('aria-hidden', String(!active));
      });
    };
    const allowGestures = () => {
      // Never make a touch target inert while a finger/animation is moving it.
      // Safari can stop delivering that gesture and leave the track between snaps.
      slides.forEach(slide => { slide.inert = false; slide.removeAttribute('aria-hidden'); });
    };
    const settle = () => {
      if (dragging) return;
      accessibility();
      indicators(); onSettle(carousel.selectedScrollSnap());
      if (announce && status) status.textContent = `${label} ${carousel.selectedScrollSnap() + 1} of ${count()}`;
      start();
    };
    const show = (index, manual = true, jump = reduced.matches) => {
      stop(); announce = manual; requested = false; remaining = interval; paint();
      const destination = Math.max(0, Math.min(count() - 1, index));
      const unchanged = carousel.selectedScrollSnap() === destination;
      carousel.scrollTo(destination, jump); indicators();
      if (jump || unchanged) settle();
    };
    const rebuild = () => {
      // Keep focused dots intact when only dimensions (not snap count) change.
      if (dots.length !== count()) {
        const focused = dots.indexOf(document.activeElement);
        dots = Array.from({ length: count() }, (_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button'; dot.className = 'sf-slider-dot'; dot.dataset.sliderDot = String(i);
          dot.setAttribute('aria-label', `Show ${label.toLowerCase()} ${i + 1} of ${count()}`);
          if (viewport.id) dot.setAttribute('aria-controls', viewport.id);
          const mark = document.createElement('span'); mark.setAttribute('aria-hidden', 'true'); dot.append(mark);
          return dot;
        });
        dotsHost?.replaceChildren(...dots);
        if (focused >= 0) (count() > 1 ? dots[Math.min(focused, dots.length - 1)] : viewport)?.focus({ preventScroll: true });
      }
      if (controls) controls.hidden = count() < 2;
      if (timerControl) timerControl.hidden = count() < 2 || root.dataset.autoplay !== 'true' || reduced.matches;
      root.dataset.sliderPages = String(count());
      root.setAttribute('aria-roledescription', count() > 1 ? 'carousel' : 'group');
      viewport.tabIndex = count() > 1 ? 0 : -1;
      stop(); remaining = interval; settle(); onResize();
    };
    carousel.on('select', () => { stop(); remaining = interval; allowGestures(); indicators(); });
    carousel.on('settle', settle).on('reInit', rebuild);
    carousel.on('pointerDown', () => { dragging = true; announce = true; requested = false; stop(); allowGestures(); viewport.classList.add('is-dragging'); });
    carousel.on('pointerUp', () => {
      dragging = false; remaining = interval; viewport.classList.remove('is-dragging');
      // A tap/no-op drag may not emit settle; restore its accessibility now.
      const engine = carousel.internalEngine();
      if (Math.abs(engine.target.get() - engine.location.get()) < .5) settle();
    });
    on(previous, 'click', () => show(carousel.selectedScrollSnap() - 1));
    on(next, 'click', () => show(carousel.selectedScrollSnap() + 1));
    on(dotsHost, 'click', event => { const dot = event.target.closest('[data-slider-dot]'); if (dot) show(Number(dot.dataset.sliderDot)); });
    on(root, 'keydown', event => {
      if (count() < 2 || event.target.closest('input,textarea,select') || event.altKey || event.ctrlKey || event.metaKey) return;
      const index = carousel.selectedScrollSnap();
      const destinations = { ArrowLeft: index - 1, ArrowRight: index + 1, Home: 0, End: count() - 1 };
      if (event.key in destinations) { event.preventDefault(); show(destinations[event.key]); }
    });
    on(root, 'pointerenter', event => { if (event.pointerType === 'mouse') { hovered = true; stop(); } });
    on(root, 'pointerleave', event => { if (event.pointerType === 'mouse') { hovered = false; requested = false; start(); } });
    on(root, 'focusin', stop);
    on(root, 'focusout', event => { if (!root.contains(event.relatedTarget)) { requested = false; queueMicrotask(start); } });
    on(document, 'visibilitychange', start);
    on(timerControl, 'click', () => {
      userPaused = !userPaused; requested = !userPaused;
      timerControl.setAttribute('aria-pressed', String(userPaused));
      timerControl.setAttribute('aria-label', userPaused ? 'Resume slideshow timer' : 'Pause slideshow timer');
      start();
    });
    on(reduced, 'change', () => carousel.reInit({ duration: reduced.matches ? 0 : 25 }));
    // Preserve native browser zoom; the horizontal wheel plugin must not intercept it.
    on(viewport, 'wheel', event => { if (event.ctrlKey || event.metaKey) event.stopImmediatePropagation(); }, { capture: true, passive: true });
    const visibility = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting && entry.intersectionRatio >= .25; start(); }, { threshold: [0, .25] });
    visibility.observe(root);
    const resize = new ResizeObserver(() => { if (!dragging) onResize(); });
    slides.forEach(slide => resize.observe(slide));
    on(window, 'resize', onResize);
    document.fonts?.ready.then(() => { if (!events.signal.aborted) onResize(); });
    rebuild();
    const api = {
      carousel, show,
      selectSlide(index) {
        editorPaused = true;
        // Embla groups product cards into pages; editor selection must map to its page.
        const page = carousel.internalEngine().slideRegistry.findIndex(indices => indices.includes(index));
        show(Math.max(0, page), true, true);
      },
      deselect() { editorPaused = false; start(); },
      destroy() {
        stop(); events.abort(); visibility.disconnect(); resize.disconnect(); carousel.destroy();
        slides.forEach(slide => { slide.inert = false; slide.removeAttribute('aria-hidden'); });
        viewport.style.height = ''; viewport.removeAttribute('tabindex'); viewport.classList.remove('is-dragging');
        root.removeAttribute('aria-roledescription');
        if (controls) controls.hidden = true;
        if (timerControl) timerControl.hidden = true;
        delete root.sfSlider;
      }
    };
    root.sfSlider = api;
    return api;
  };
})();
